import styled           from 'styled-components/macro'
import { useEffect, useRef, forwardRef } from 'react'
import { useGet }       from 'state/jotai'
import { rotate_by_slider } from 'state/store.global'
import { useWindowSize } from 'hooks/useWindowResize'

export const Canvas = forwardRef(({ dim, frames, frame, pid, status, config }, ref) => {
  const isIsometryRotate = useGet(rotate_by_slider)
  const size             = useWindowSize()
  const frameMapRef      = useRef({})
  const rafRef           = useRef(null)

  // Build O(1) frame lookup map whenever frames changes
  useEffect(() => {
    frameMapRef.current = frames.reduce((acc, f) => {
      const key = Object.keys(f)[0]
      acc[key] = f[key]
      return acc
    }, {})
  }, [frames])

  useEffect(() => {
    const ctx = ref?.current?.getContext('2d')
    if (!ctx) return

    const w   = dim.W
    const h   = dim.H
    const img = frameMapRef.current[frame]

    if (!img?.src) return

    const doDraw = () => {
      if (ctx.canvas.width !== w || ctx.canvas.height !== h) {
        ctx.canvas.width  = w
        ctx.canvas.height = h
      }
      ctx.clearRect(0, 0, w, h)
      ctx.globalAlpha = 1
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(() => {
        ctx.drawImage(img, 0, 0, w, h)
      })
    }

    if (!img.complete || img.naturalWidth === 0) {
      img.onload = doDraw
      return
    }

    doDraw()

    return () => cancelAnimationFrame(rafRef.current)
  }, [size, frame, pid, frames, status, dim, ref])

  const ready = frames?.[config?.IMAGE_NUM - 1]?.[config?.LAST_IMAGE]?.src

  return (
    <>
      {ready && (
        <StyledCanvas
          w={dim.W}
          h={dim.H}
          ref={ref}
          isIsometryRotate={isIsometryRotate}
        />
      )}
    </>
  )
})

const StyledCanvas = styled.canvas`
  width: ${x => x.w}px;
  height: ${x => x.h}px;
  max-width: unset;
  background: transparent;
  position: absolute;
`